export type MhaStatusRow = {
  id: string;
  root_patient_id: string;
  effective_date_time: string;
  event_type: string;
  mha_status: string;
  mha_status_start_date_time: string | null;
  mha_status_initial_start_date_time: string | null;
  mha_status_expiry_date_time: string | null;
  hospital_id: string | null;
  hospital_name: string;
  hospital_postcode: string;
  trust_id: string | null;
  trust_name: string | null;
  created: string;
  deceased_date_time: string | null;
};

// In-scope MHA statuses for detention (mirrors LcrPatientStateCreator)
const inScopeStatuses = [
  "Section 136",
  "Section 17A",
  "Section 17A (Recalled)",
  "Section 2",
  "Section 3",
  "Section 4",
  "Section 5(2)",
  "Section 5(4)",
];

// CTO recall event types (lowercase as per data)
const CTO_RECALL_START = "cto4"; // CTO4 = recall with definitive date
const CTO_RECALL_END_TYPES = ["cto5"]; // CTO5 = revocation
// CTO forms that indicate patient is on CTO (not recalled) - these override recall status
const CTO_INDICATING_TYPES = [
  "cto1",
  "cto2",
  "cto3",
  "cto6",
  "cto7", // CTO7 = renewal
  "cto8",
  "cto9",
  "cto10",
  "cto11",
  "cto12",
];
const CTO_RECALL_MAX_HOURS = 72; // Recall can only last 72 hours max

/**
 * Extract the form type from an event_type field.
 * event_type can be a string like "cto4" or an object like { formType: "cto4", ... }
 */
function getFormType(eventType: unknown): string | null {
  if (!eventType) return null;
  if (typeof eventType === "string") return eventType.toLowerCase();
  if (
    typeof eventType === "object" &&
    "formType" in eventType &&
    typeof eventType.formType === "string"
  )
    return eventType.formType.toLowerCase();
  return null;
}

/**
 * A consolidated period of detention, similar to IntermediaryState in LcrPatientStateCreator.
 * Represents a single continuous period under one MHA section.
 */
export type DetentionPeriod = {
  section: string;
  startDate: Date;
  endDate: Date;
  completionStatus: "active" | "expired" | "not-detained" | "completed";
  events: MhaStatusRow[];
  hospitals: Set<string>;
  trusts: Set<string>;
  /**
   * Primary provider attribution — the trust/hospital of the first event that
   * names each, as an (id, name) pair. Populated by calculateDetentionPeriods
   * (see primaryOrg); null for event-less periods (Not Detained gaps, the
   * Deceased marker). Optional so the intermediate period literals need not set
   * it — it is filled once, on the final periods, before they are returned.
   */
  hospitalId?: string | null;
  hospitalName?: string | null;
  trustId?: string | null;
  trustName?: string | null;
};

/** A resolved (id, name) provider pair. */
export type OrgAttribution = {
  hospitalId: string | null;
  hospitalName: string | null;
  trustId: string | null;
  trustName: string | null;
};

/**
 * Primary provider attribution for a set of events: the trust and hospital of
 * the FIRST event that names each. Trust and hospital are resolved
 * independently (an event may carry one but not the other), and each id is read
 * from the SAME event as its name so the (id, name) pair is always consistent.
 * Empty for periods with no backing events (Not Detained gaps, Deceased marker).
 */
export function primaryOrg(events: MhaStatusRow[]): OrgAttribution {
  const h = events.find((e) => e.hospital_name);
  const t = events.find((e) => e.trust_name);
  return {
    hospitalId: h?.hospital_id ?? null,
    hospitalName: h?.hospital_name ?? null,
    trustId: t?.trust_id ?? null,
    trustName: t?.trust_name ?? null,
  };
}

// Sections that aren't a detention: their span is a gap or a marker, so a
// negative one is an overlap artifact, not a negative detention.
const NON_DETENTION_MARKERS = new Set(["Not Detained", "Expired", "Deceased"]);

/**
 * Optional accumulator for derivation-level data-quality signals that are
 * *emergent* — only knowable inside calculateDetentionPeriods, and erased by
 * clampNonNegative before any downstream check runs on the emitted periods.
 * Negative-duration periods (contradictory backdated starts, overlapping source
 * detentions) are counted here, split the same way the durationChecks do. A
 * caller passes ONE mutable object across many calls to total across patients.
 */
export type DerivationDiagnostics = {
  negativeDetention: number;
  negativeGapOverlap: number;
};

/**
 * Clean and consolidate raw MHA events into detention periods.
 * Based on LcrPatientStateCreator.calculateIntermediaryStates logic:
 * - Consecutive events of the same section are merged into one period
 * - Transition to a different section completes the current period
 * - Not Detained/Expired status ends the current period
 */
export function calculateDetentionPeriods(
  entries: MhaStatusRow[],
  opts: { asOf?: Date; diagnostics?: DerivationDiagnostics } = {},
): DetentionPeriod[] {
  // Reference "now" for capping still-active periods. Injectable so the
  // derivation is deterministic (nightly batch / tests / back-dating); defaults
  // to the real clock to preserve existing caller behaviour.
  const asOf = opts.asOf ?? new Date();
  let activePeriod: DetentionPeriod | null = null;
  const allPeriods: DetentionPeriod[] = [];
  // Track whether the active period saw an Incomplete event, meaning
  // it should auto-expire at its expiry date rather than stretching
  let sawIncomplete = false;

  // Process events in clinical chronological order (effective time), NOT data-
  // entry order (`created`). A discharge or conversion is frequently backfilled
  // days/months after the fact — sorting by `created` then lands it out of
  // clinical sequence, so a Not Detained between two same-section detentions
  // gets processed out of place (even dropped, if it sorts before the opening
  // event with no active period) and the two detentions wrongly merge into one.
  // Effective time keeps a backdated conversion's discharge ahead of the
  // backdated section (the out-gap logic below relies on that order); the
  // same-section "lapse" split handles re-detentions whose historical window
  // (`initial_start`) predates their import. `created`, then `id`, are
  // deterministic tiebreakers so simultaneous events never fall to input order.
  const sortedEntries = [...entries].sort((a, b) => {
    const byEffective =
      new Date(a.effective_date_time).getTime() -
      new Date(b.effective_date_time).getTime();
    if (byEffective !== 0) return byEffective;
    const byCreated =
      new Date(a.created).getTime() - new Date(b.created).getTime();
    if (byCreated !== 0) return byCreated;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  // Helper: if the active period had an Incomplete and a new event arrives
  // after the expiry, close the period at its expiry and insert ND
  function handleIncompleteGap(newEventStart: Date): void {
    if (!activePeriod || !sawIncomplete) return;
    // sawIncomplete is only set on an in-scope detention, so activePeriod is a
    // detention here (never Not Detained / Expired).

    const expiry = activePeriod.endDate;
    if (newEventStart > expiry) {
      // New event starts after expiry — close at expiry, insert ND gap
      activePeriod.completionStatus = "expired";
      allPeriods.push(activePeriod);

      activePeriod = {
        section: "Not Detained",
        startDate: expiry,
        endDate: newEventStart,
        completionStatus: "active",
        events: [],
        hospitals: new Set(),
        trusts: new Set(),
      };
      sawIncomplete = false;
    }
    // If new event starts before/at expiry, transition normally
  }

  for (const entry of sortedEntries) {
    const isInScopeStatus = inScopeStatuses.includes(entry.mha_status);
    const isEndingStatus =
      entry.mha_status === "Not Detained" || entry.mha_status === "Expired";
    const isIncomplete = entry.mha_status === "Incomplete";

    if (activePeriod === null) {
      // No active period - start one if this is an in-scope status
      if (isInScopeStatus) {
        activePeriod = {
          section: entry.mha_status,
          startDate: new Date(
            entry.mha_status_initial_start_date_time ??
              entry.mha_status_start_date_time ??
              entry.effective_date_time,
          ),
          endDate: new Date(
            entry.mha_status_expiry_date_time ?? entry.effective_date_time,
          ),
          completionStatus: "active",
          events: [entry],
          hospitals: new Set(entry.hospital_name ? [entry.hospital_name] : []),
          trusts: new Set(entry.trust_name ? [entry.trust_name] : []),
        };
        sawIncomplete = false;
      }
    } else if (isIncomplete) {
      // Incomplete means paperwork wasn't completed — the previous detention
      // should expire at its natural expiry date, not stretch to the next event.
      // Just flag it; the expiry/ND insertion happens when the next event arrives.
      if (
        activePeriod.section !== "Not Detained" &&
        activePeriod.section !== "Expired"
      ) {
        sawIncomplete = true;
        activePeriod.events.push(entry);
      }
    } else {
      // We have an active period.
      // Does this period's endDate reflect a REAL stored expiry, or is it just a
      // placeholder — the opening event's start/effective time, used when no
      // `mha_status_expiry_date_time` was recorded? The "authority lapsed" splits
      // below all hinge on the end being a true statutory expiry. When none is
      // known the end equals the start, so a later event would spuriously look
      // like a lapse and collapse the detention to zero length (a null-expiry
      // Section 3 discharged 90 days later would report 0 days). Only infer a
      // lapse when an expiry is actually known.
      const activeExpiryKnown = activePeriod.events.some((e) =>
        Boolean(e.mha_status_expiry_date_time),
      );
      if (isEndingStatus) {
        handleIncompleteGap(new Date(entry.effective_date_time));

        // A detention never outlives its own authority. If this discharge/expiry
        // is recorded AFTER the section's expiry, the authority already lapsed at
        // the expiry — close it there, and the late event merely formalises a
        // not-detained state that began then. s23 discharges carry no reference to
        // the power they end, so a late s23 that actually discharged a *later,
        // unrecorded* full section must not stretch this one past its statutory
        // limit (the "468-day Section 2"). Only a detention with a KNOWN expiry
        // has a meaningful cap; an already Not Detained/Expired period, or a
        // detention whose expiry was never recorded, just tracks event time.
        const eventTime = new Date(entry.effective_date_time);
        const isDetention: boolean =
          activePeriod.section !== "Not Detained" &&
          activePeriod.section !== "Expired";
        const lapsed: boolean =
          isDetention && activeExpiryKnown && eventTime > activePeriod.endDate;
        const endDate: Date = lapsed ? activePeriod.endDate : eventTime;
        activePeriod.completionStatus = lapsed
          ? "expired"
          : entry.mha_status === "Not Detained"
            ? "not-detained"
            : "expired";
        activePeriod.endDate = endDate;
        activePeriod.events.push(entry);
        allPeriods.push(activePeriod);

        // Start a "Not Detained" period from the (capped) end.
        activePeriod = {
          section: entry.mha_status, // "Not Detained" or "Expired"
          startDate: endDate,
          endDate: endDate, // Will be updated when next detention starts
          completionStatus: "active",
          events: [entry],
          hospitals: new Set(),
          trusts: new Set(),
        };
        sawIncomplete = false;
      } else if (entry.mha_status === activePeriod.section) {
        // Same section. A renewal / duplicate / late form update has authority
        // CONTIGUOUS with the current period (its start falls at or before the
        // current end) — extend, taking the widest envelope. But if its authority
        // begins AFTER the current period has already ended, the prior authority
        // lapsed: this is a fresh RE-DETENTION under the same section, not a
        // renewal. Close the current period (expired) with a Not Detained gap and
        // open a new period, rather than gluing two separate detentions into one
        // (e.g. a 2018 and a 2024 Section 2 becoming a single 6-year period).
        const newStart = new Date(
          entry.mha_status_initial_start_date_time ??
            entry.mha_status_start_date_time ??
            entry.effective_date_time,
        );
        if (activeExpiryKnown && newStart > activePeriod.endDate) {
          const lapseAt = activePeriod.endDate;
          activePeriod.completionStatus = "expired";
          allPeriods.push(activePeriod);
          // Not Detained gap between the lapsed authority and the re-detention,
          // so the two read as separate episodes downstream.
          allPeriods.push({
            section: "Not Detained",
            startDate: lapseAt,
            endDate: newStart,
            completionStatus: "active",
            events: [],
            hospitals: new Set(),
            trusts: new Set(),
          });
          activePeriod = {
            section: entry.mha_status,
            startDate: newStart,
            endDate: new Date(
              entry.mha_status_expiry_date_time ?? entry.effective_date_time,
            ),
            completionStatus: "active",
            events: [entry],
            hospitals: new Set(
              entry.hospital_name ? [entry.hospital_name] : [],
            ),
            trusts: new Set(entry.trust_name ? [entry.trust_name] : []),
          };
          sawIncomplete = false;
        } else {
          // Contiguous continuation — extend the current period.
          activePeriod.events.push(entry);
          if (entry.hospital_name)
            activePeriod.hospitals.add(entry.hospital_name);
          if (entry.trust_name) activePeriod.trusts.add(entry.trust_name);
          if (entry.mha_status_expiry_date_time) {
            // Max-guard, symmetric with the start min-guard below: a same-section
            // continuation only ever EXTENDS the period end, never pulls it
            // earlier. A later event can carry an earlier expiry.
            const newEnd = new Date(entry.mha_status_expiry_date_time);
            if (newEnd > activePeriod.endDate) {
              activePeriod.endDate = newEnd;
            }
          }
          if (newStart < activePeriod.startDate) {
            activePeriod.startDate = newStart;
          }
        }
      } else if (isInScopeStatus) {
        // Different section - complete current period and start new one
        const newStartDateTime = new Date(
          entry.mha_status_initial_start_date_time ??
            entry.mha_status_start_date_time ??
            entry.effective_date_time,
        );

        handleIncompleteGap(newStartDateTime);

        // If current period is Not Detained/Expired, set its end to the new detention start
        if (
          activePeriod.section === "Not Detained" ||
          activePeriod.section === "Expired"
        ) {
          activePeriod.endDate = newStartDateTime;
          if (activePeriod.endDate > activePeriod.startDate) {
            allPeriods.push(activePeriod);
          } else {
            // The next detention's (often backdated) start is at/before the
            // out-period's start — so there was no real "not detained" gap: this
            // is a backdated conversion/renewal, not a discharge. Drop the empty
            // out-period and reconcile with the prior detention, trusting the
            // newer record's stated start (as the in-range pull-back already
            // does). (A negative gap would otherwise be a common, meaningless
            // artifact — the standalone visualiser hid these at render time.)
            const prior = allPeriods[allPeriods.length - 1];
            if (prior && newStartDateTime < prior.endDate) {
              if (newStartDateTime >= prior.startDate) {
                // Backdated into the prior detention — pull its end back to the
                // new start so the timeline stays adjacent rather than overlapping.
                prior.endDate = newStartDateTime;
              } else {
                // Backdated before the prior detention even began: a patient
                // can't hold two sections at once and the later record is the
                // correction, so it supersedes the prior period it fully covers.
                // Drop that period, letting the new section open at its stated
                // (backdated) start rather than overlapping it or an invented one.
                allPeriods.pop();
              }
            }
          }
        } else if (
          activeExpiryKnown &&
          newStartDateTime > activePeriod.endDate
        ) {
          // The current detention's authority expired before this new section
          // began — a lapse, not a conversion. Close it at its own expiry with a
          // Not Detained gap rather than stretching the period across the gap to
          // the new section's start (which would glue, e.g., an S2 and an S3 15
          // months apart into one ~year-long S2). Mirrors the same-section lapse
          // split above and handleIncompleteGap's behaviour for Incomplete.
          const lapseAt = activePeriod.endDate;
          activePeriod.completionStatus = "expired";
          allPeriods.push(activePeriod);
          allPeriods.push({
            section: "Not Detained",
            startDate: lapseAt,
            endDate: newStartDateTime,
            completionStatus: "active",
            events: [],
            hospitals: new Set(),
            trusts: new Set(),
          });
        } else {
          // Regular contiguous section transition (conversion within authority).
          activePeriod.completionStatus = "completed";
          activePeriod.endDate = newStartDateTime;
          allPeriods.push(activePeriod);
        }

        activePeriod = {
          section: entry.mha_status,
          startDate: newStartDateTime,
          endDate: new Date(
            entry.mha_status_expiry_date_time ?? entry.effective_date_time,
          ),
          completionStatus: "active",
          events: [entry],
          hospitals: new Set(entry.hospital_name ? [entry.hospital_name] : []),
          trusts: new Set(entry.trust_name ? [entry.trust_name] : []),
        };
        sawIncomplete = false;
      }
      // Ignore Unknown or other non-actionable statuses
    }
  }

  // Push any remaining active period
  if (activePeriod) {
    if (
      sawIncomplete &&
      activePeriod.completionStatus === "active" &&
      activePeriod.section !== "Not Detained" &&
      activePeriod.section !== "Expired"
    ) {
      // Incomplete was the last meaningful event — expire at natural expiry, then ND
      activePeriod.completionStatus = "expired";
      const expiry = activePeriod.endDate;
      allPeriods.push(activePeriod);
      allPeriods.push({
        section: "Not Detained",
        startDate: expiry,
        endDate: expiry,
        completionStatus: "active",
        events: [],
        hospitals: new Set(),
        trusts: new Set(),
      });
    } else {
      // The trailing period is always still active; cap its end to asOf.
      if (activePeriod.endDate > asOf) {
        activePeriod.endDate = asOf;
      }
      allPeriods.push(activePeriod);
    }
  }

  const derived = extendTrailingOutPeriod(
    consolidatePeriods(
      capNonExtensiblePeriods(
        capSection136Periods(splitCtoPeriodsAtRecalls(allPeriods)),
      ),
    ),
    asOf,
  );

  // Count negative-duration periods BEFORE clampNonNegative collapses them — the
  // downstream data-quality checks run on the emitted (clamped) periods and so
  // could never see them otherwise. Split by detention vs marker, matching the
  // negative_duration_detention / negative_gap_overlap checks.
  if (opts.diagnostics) {
    for (const p of derived) {
      if (p.endDate < p.startDate) {
        if (NON_DETENTION_MARKERS.has(p.section)) {
          opts.diagnostics.negativeGapOverlap++;
        } else {
          opts.diagnostics.negativeDetention++;
        }
      }
    }
  }

  const periods = clampNonNegative(derived);
  const finalPeriods = applyDeceased(periods, getDeceasedDateTime(entries));

  // Attribute each final period to its primary trust/hospital (id + name) from
  // its events — done once here so both the detention-period output and the
  // episodes derived from these periods share one consistent (id, name) pair.
  for (const p of finalPeriods) {
    const org = primaryOrg(p.events);
    p.hospitalId = org.hospitalId;
    p.hospitalName = org.hospitalName;
    p.trustId = org.trustId;
    p.trustName = org.trustName;
  }
  return finalPeriods;
}

/**
 * Extract the patient-level death date from a set of events, if present.
 * deceased_date_time is denormalised onto every row, so the first non-null
 * value is taken. Returns null for living patients.
 */
export function getDeceasedDateTime(entries: MhaStatusRow[]): Date | null {
  const raw = entries.find((e) => e.deceased_date_time)?.deceased_date_time;
  return raw ? new Date(raw) : null;
}

/**
 * Apply death to a derived set of periods: remove periods that start at/after
 * death, truncate any period that spans death, and append a synthetic
 * zero-duration "Deceased" period. The Deceased period is an *inferred* marker
 * (no backing events), not a recorded state. No-op for living patients.
 *
 * Returns a new list. The periods it keeps are the caller's own objects, whose
 * endDate is truncated in place.
 *
 * The marker + truncated ends carry the EXACT death instant here — accurate for
 * the derivation. The preloader fuzzes them to week granularity before persisting
 * (see fuzzDeceasedDates); nothing exact-death is written to the output DB.
 */
function applyDeceased(
  periods: DetentionPeriod[],
  deceasedDateTime: Date | null,
): DetentionPeriod[] {
  if (!deceasedDateTime) return periods;

  // Removal is keyed on "demonstrably starts at or after death", not on the
  // apparent inverse "starts before death": an unparseable date makes both
  // comparisons false, and a period we cannot place must stay in the timeline
  // for the data-quality checks to see rather than silently disappear from it.
  const survivors = periods.filter((p) => !(p.startDate >= deceasedDateTime));
  for (const p of survivors) {
    if (p.endDate > deceasedDateTime) {
      p.endDate = deceasedDateTime;
    }
  }

  survivors.push({
    section: "Deceased",
    startDate: deceasedDateTime,
    endDate: deceasedDateTime,
    completionStatus: "completed",
    events: [],
    hospitals: new Set(),
    trusts: new Set(),
  });

  return survivors;
}

/**
 * The patient remains out from their last discharge until now, so extend a
 * trailing active "Not Detained" period to asOf. Without this a still-open
 * out-period is a zero-duration marker; extending it makes "time out so far"
 * (e.g. time-to-re-detention) measurable. applyDeceased still truncates it at
 * death where relevant.
 */
function extendTrailingOutPeriod(
  periods: DetentionPeriod[],
  asOf: Date,
): DetentionPeriod[] {
  // A trailing "Not Detained" period is the open "currently out" tail.
  const last = periods[periods.length - 1];
  if (last && last.section === "Not Detained" && last.endDate < asOf) {
    last.endDate = asOf;
  }
  return periods;
}

/**
 * Final timeline consolidation, producing a matching-ready period list:
 * - "Expired" collapses to "Not Detained" (clinically the same not-under-section
 *   state; the original Expired event remains in events[]).
 * - Consecutive same-section periods are merged into one.
 * This is the canonical timeline; downstream (e.g. the pathway DSL) consumes it
 * as-is and does not re-consolidate.
 */
function consolidatePeriods(periods: DetentionPeriod[]): DetentionPeriod[] {
  const result: DetentionPeriod[] = [];
  for (const period of periods) {
    const section =
      period.section === "Expired" ? "Not Detained" : period.section;
    const last = result[result.length - 1];
    if (last && last.section === section) {
      // Merges any consecutive same-section periods. In practice that's only
      // Not Detained / Expired (which carry no hospital/trust), but union the
      // locations defensively so a same-section *detention* merge can never
      // silently drop the later period's hospitals/trusts (PR review).
      last.endDate = period.endDate;
      last.completionStatus = period.completionStatus;
      last.events = [...last.events, ...period.events];
      period.hospitals.forEach((h) => last.hospitals.add(h));
      period.trusts.forEach((t) => last.trusts.add(t));
    } else {
      result.push({ ...period, section });
    }
  }
  return result;
}

/**
 * Post-process Section 136 periods to cap them at their expiry time
 * and insert an implicit "Not Detained" period afterwards.
 * S136 events have a fixed 24h duration with no explicit ending event,
 * so without this the S136 period would stretch until the next event.
 */
function capSection136Periods(periods: DetentionPeriod[]): DetentionPeriod[] {
  const result: DetentionPeriod[] = [];

  for (const [i, period] of periods.entries()) {
    if (period.section !== "Section 136") {
      result.push(period);
      continue;
    }

    // Get the actual 24h expiry from the S136 event data, since
    // calculateDetentionPeriods may have overwritten period.endDate
    // with the next section's start time
    const s136Event = period.events.find((e) => e.mha_status === "Section 136");
    const expiryRaw = s136Event?.mha_status_expiry_date_time;
    if (!expiryRaw) {
      result.push(period);
      continue;
    }
    const expiryTime = new Date(expiryRaw);

    const nextPeriod = periods[i + 1];
    const nextStart = nextPeriod?.startDate;

    if (!nextStart || nextStart > expiryTime) {
      // No next period, or next period starts after S136 expiry → cap and insert ND
      result.push({
        ...period,
        endDate: expiryTime,
        completionStatus: "completed",
      });
      result.push({
        section: "Not Detained",
        startDate: expiryTime,
        endDate: nextStart ?? expiryTime,
        completionStatus: "active",
        events: [],
        hospitals: new Set(),
        trusts: new Set(),
      });
    } else {
      // Next event starts within/at the S136 24h window — cap at expiry but no ND gap
      result.push({
        ...period,
        // In this branch nextStart <= expiryTime, so the cap is always nextStart.
        endDate: nextStart,
        completionStatus: "completed",
      });
    }
  }

  return result;
}

// Statutory maximum duration (hours) for powers that CANNOT be extended, so a
// stored expiry beyond this is always a data error. Section 136 is excluded — it
// can be extended to 36h — and is handled by capSection136Periods on its stored
// expiry. S2/S3/CTO are excluded: S2 can be court-extended (s29(4)) and S3/CTO
// are renewable, so their (possibly-extended) stored expiry is authoritative.
const NON_EXTENSIBLE_MAX_HOURS: Record<string, number> = {
  "Section 4": 72,
  "Section 5(2)": 72,
  "Section 5(4)": 6,
};

/**
 * Hard-cap non-extensible holding/emergency powers (5(2), 5(4), 4) at their
 * statutory maximum. Unlike a court-extendable S2 or a renewable S3, these can
 * never lawfully run past a fixed limit, so any period longer than the limit —
 * whether from a wrong stored expiry or a late-recorded discharge that the
 * upstream cap missed — is clamped, with a Not Detained gap for the remainder.
 * An earlier end (early conversion/discharge within the window) is preserved.
 */
function capNonExtensiblePeriods(
  periods: DetentionPeriod[],
): DetentionPeriod[] {
  const result: DetentionPeriod[] = [];

  for (const [i, period] of periods.entries()) {
    const maxHours = NON_EXTENSIBLE_MAX_HOURS[period.section];
    if (maxHours === undefined) {
      result.push(period);
      continue;
    }
    const statutoryEnd = new Date(
      period.startDate.getTime() + maxHours * 3_600_000,
    );
    if (period.endDate <= statutoryEnd) {
      result.push(period);
      continue;
    }

    result.push({
      ...period,
      endDate: statutoryEnd,
      completionStatus: "expired",
    });
    const nextStart = periods[i + 1]?.startDate;
    if (!nextStart || nextStart > statutoryEnd) {
      result.push({
        section: "Not Detained",
        startDate: statutoryEnd,
        endDate: nextStart ?? statutoryEnd,
        completionStatus: "active",
        events: [],
        hospitals: new Set(),
        trusts: new Set(),
      });
    }
  }

  return result;
}

/**
 * Invariant: no period may end before it starts. A contradictory backdated start
 * — a later section recorded as beginning before the currently-active period
 * (typo, or an expiry override that rewrites the start; see data-quality) — could
 * otherwise pull a period's end behind its start and emit a negative duration
 * (e.g. a "-365 day Section 3"). Clamp to zero length so the emitted timeline is
 * never negative. The impossible span is not lost: it's counted into the
 * DerivationDiagnostics sink (see calculateDetentionPeriods) BEFORE this runs, so
 * the data-quality checks can still report it. Zero-length periods are left
 * as-is: a sparse transition event (a section with only an effective date)
 * legitimately has equal start/end, so they must not be dropped.
 */
function clampNonNegative(periods: DetentionPeriod[]): DetentionPeriod[] {
  for (const period of periods) {
    if (period.endDate < period.startDate) {
      period.endDate = period.startDate;
    }
  }
  return periods;
}

/**
 * Add an event's hospital and trust to the running sets, when present.
 */
function addLocation(
  hospitals: Set<string>,
  trusts: Set<string>,
  event: MhaStatusRow,
): void {
  if (event.hospital_name) hospitals.add(event.hospital_name);
  if (event.trust_name) trusts.add(event.trust_name);
}

/**
 * Post-process CTO periods to split them at recall boundaries.
 * CTO4 events mark recall start. Recall ends at:
 * - CTO5/CTO7 event
 * - Section 3 status change (revocation)
 * - 72 hours from CTO4 (auto-expire if nothing else happens)
 */
function splitCtoPeriodsAtRecalls(
  periods: DetentionPeriod[],
): DetentionPeriod[] {
  const result: DetentionPeriod[] = [];

  for (const [i, period] of periods.entries()) {
    // Only process Section 17A periods
    if (period.section !== "Section 17A") {
      result.push(period);
      continue;
    }

    // Check if next period is Section 3 (indicates revocation)
    const nextPeriod = periods[i + 1];
    const isTransitionToSection3 =
      nextPeriod && nextPeriod.section === "Section 3";

    // Find CTO4 (recall start) events within this period
    const recallEvents = period.events.filter(
      (e) => getFormType(e.event_type) === CTO_RECALL_START,
    );

    if (recallEvents.length === 0) {
      // No recalls in this CTO period
      if (isTransitionToSection3) {
        // CTO → Section 3 without any recall events - insert implicit zero-duration recall
        // This happens when recall paperwork (CTO4) is completely missing
        result.push({
          ...period,
          completionStatus: "completed",
        });
        result.push({
          section: "Section 17A (Recalled)",
          startDate: period.endDate,
          endDate: period.endDate,
          completionStatus: "completed",
          events: [],
          hospitals: new Set(),
          trusts: new Set(),
        });
      } else {
        result.push(period);
      }
      continue;
    }

    // Sort events by date for processing
    const sortedEvents = [...period.events].sort(
      (a, b) =>
        new Date(a.effective_date_time).getTime() -
        new Date(b.effective_date_time).getTime(),
    );

    // Track current state and build sub-periods
    let currentStart = period.startDate;
    let currentEvents: MhaStatusRow[] = [];
    let currentHospitals = new Set<string>();
    let currentTrusts = new Set<string>();
    let isRecalled = false;
    let recallStartTime: Date | null = null;
    // Set when a CTO5 revokes the recall — suppresses the trailing sub-period
    // so we don't re-emit an overlapping period after the revocation break.
    let revoked = false;

    for (const event of sortedEvents) {
      const formType = getFormType(event.event_type);
      const eventDate = new Date(event.effective_date_time);

      // Check if recall has exceeded 72 hours - auto-end it before processing this event
      // BUT: don't auto-close if current event is another CTO4 (same recall, duplicate paperwork)
      // recallStartTime is always set while isRecalled is true; the null check
      // is what narrows it for the compiler.
      if (
        isRecalled &&
        recallStartTime !== null &&
        formType !== CTO_RECALL_START
      ) {
        const maxRecallEnd = new Date(
          recallStartTime.getTime() + CTO_RECALL_MAX_HOURS * 60 * 60 * 1000,
        );
        if (eventDate > maxRecallEnd) {
          // 72 hours exceeded - close recalled period at max time
          result.push({
            section: "Section 17A (Recalled)",
            startDate: currentStart,
            endDate: maxRecallEnd,
            completionStatus: "completed",
            events: currentEvents,
            hospitals: currentHospitals,
            trusts: currentTrusts,
          });

          // Start new CTO period from max recall end
          currentStart = maxRecallEnd;
          currentEvents = [];
          currentHospitals = new Set();
          currentTrusts = new Set();
          isRecalled = false;
          recallStartTime = null;
        }
      }

      if (formType === CTO_RECALL_START && !isRecalled) {
        // Recall starts - close current CTO period, start recalled period
        if (currentEvents.length > 0 || eventDate > currentStart) {
          result.push({
            section: "Section 17A",
            startDate: currentStart,
            endDate: eventDate,
            completionStatus: "completed",
            events: currentEvents,
            hospitals: currentHospitals,
            trusts: currentTrusts,
          });
        }

        // Start recalled period
        currentStart = eventDate;
        recallStartTime = eventDate;
        currentEvents = [event];
        currentHospitals = new Set(
          event.hospital_name ? [event.hospital_name] : [],
        );
        currentTrusts = new Set(event.trust_name ? [event.trust_name] : []);
        isRecalled = true;
      } else if (formType === CTO_RECALL_START && isRecalled) {
        // Additional CTO4 while already recalled - just add as event, don't start new recall
        currentEvents.push(event);
        addLocation(currentHospitals, currentTrusts, event);
      } else if (CTO_RECALL_END_TYPES.includes(formType || "") && isRecalled) {
        // CTO5 revocation - close recalled period and stop processing
        currentEvents.push(event);
        addLocation(currentHospitals, currentTrusts, event);

        result.push({
          section: "Section 17A (Recalled)",
          startDate: currentStart,
          endDate: eventDate,
          completionStatus: "completed",
          events: currentEvents,
          hospitals: currentHospitals,
          trusts: currentTrusts,
        });

        // CTO5 = revocation: this CTO period ends here; the next period
        // (Section 3) comes from the regular period sequence.
        revoked = true;
        break;
      } else if (CTO_RECALL_END_TYPES.includes(formType || "") && !isRecalled) {
        // CTO5 (revocation) without a preceding CTO4 recall - missing recall
        // paperwork. (CTO7 is handled separately via CTO_INDICATING_TYPES.)
        // Insert implicit zero-duration recall for DSL matching purposes
        currentEvents.push(event);
        addLocation(currentHospitals, currentTrusts, event);

        // Close current CTO period
        if (eventDate > currentStart) {
          result.push({
            section: "Section 17A",
            startDate: currentStart,
            endDate: eventDate,
            completionStatus: "completed",
            events: currentEvents.slice(0, -1), // exclude the CTO5 event
            hospitals: currentHospitals,
            trusts: currentTrusts,
          });
        }

        // Insert implicit zero-duration recall
        result.push({
          section: "Section 17A (Recalled)",
          startDate: eventDate,
          endDate: eventDate,
          completionStatus: "completed",
          events: [event],
          hospitals: new Set(event.hospital_name ? [event.hospital_name] : []),
          trusts: new Set(event.trust_name ? [event.trust_name] : []),
        });

        // CTO5 = revocation: this CTO period ends here; the next period
        // (Section 3) comes from the regular period sequence.
        revoked = true;
        break;
      } else if (CTO_INDICATING_TYPES.includes(formType || "") && isRecalled) {
        // CTO-indicating event while recalled - ends recall, returns to CTO
        currentEvents.push(event);
        addLocation(currentHospitals, currentTrusts, event);

        result.push({
          section: "Section 17A (Recalled)",
          startDate: currentStart,
          endDate: eventDate,
          completionStatus: "completed",
          events: currentEvents,
          hospitals: currentHospitals,
          trusts: currentTrusts,
        });

        // Start new CTO period
        currentStart = eventDate;
        currentEvents = [];
        currentHospitals = new Set();
        currentTrusts = new Set();
        isRecalled = false;
        recallStartTime = null;
      } else {
        // Regular event within current period
        currentEvents.push(event);
        addLocation(currentHospitals, currentTrusts, event);
      }
    }

    // Push final sub-period (skipped if a CTO5 revoked the period above)
    if (
      !revoked &&
      (currentEvents.length > 0 || currentStart < period.endDate)
    ) {
      // recallStartTime is always set while isRecalled is true; the null check
      // is what narrows it for the compiler.
      if (isRecalled && recallStartTime !== null) {
        const maxRecallEnd = new Date(
          recallStartTime.getTime() + CTO_RECALL_MAX_HOURS * 60 * 60 * 1000,
        );

        // Recall is always capped at 72 hours
        const recallEnd = new Date(
          Math.min(maxRecallEnd.getTime(), period.endDate.getTime()),
        );

        result.push({
          section: "Section 17A (Recalled)",
          startDate: currentStart,
          endDate: recallEnd,
          completionStatus: "completed",
          events: currentEvents,
          hospitals: currentHospitals,
          trusts: currentTrusts,
        });

        // If recall ended before period ended (72h cap), add remaining CTO time
        if (recallEnd < period.endDate) {
          result.push({
            section: "Section 17A",
            startDate: recallEnd,
            endDate: period.endDate,
            completionStatus: isTransitionToSection3
              ? "completed"
              : period.completionStatus,
            events: [],
            hospitals: new Set(),
            trusts: new Set(),
          });

          // If transitioning to Section 3 after the auto-closed recall,
          // we need an implicit recall at the CTO→S3 boundary
          if (isTransitionToSection3) {
            result.push({
              section: "Section 17A (Recalled)",
              startDate: period.endDate,
              endDate: period.endDate,
              completionStatus: "completed",
              events: [],
              hospitals: new Set(),
              trusts: new Set(),
            });
          }
        } else if (isTransitionToSection3) {
          // Recall ended exactly at period end AND transitioning to S3
          // The recall properly ends at the revocation
        }
      } else {
        // Not currently in recalled state
        if (isTransitionToSection3) {
          // CTO → Section 3 without explicit recall - insert implicit zero-duration recall
          // This happens when recall paperwork (CTO4) is missing but revocation occurred
          result.push({
            section: "Section 17A",
            startDate: currentStart,
            endDate: period.endDate,
            completionStatus: "completed",
            events: currentEvents,
            hospitals: currentHospitals,
            trusts: currentTrusts,
          });

          // Insert implicit zero-duration recall at the boundary
          result.push({
            section: "Section 17A (Recalled)",
            startDate: period.endDate,
            endDate: period.endDate,
            completionStatus: "completed",
            events: [],
            hospitals: new Set(),
            trusts: new Set(),
          });
        } else {
          result.push({
            section: "Section 17A",
            startDate: currentStart,
            endDate: period.endDate,
            completionStatus: period.completionStatus,
            events: currentEvents,
            hospitals: currentHospitals,
            trusts: currentTrusts,
          });
        }
      }
    }
  }

  return result;
}
